import { submissionApiClient } from "@/api/submission-api";
import { editDealInfo } from "@/api/deals-api";
import type { Workspace } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import {
  StepCard as Card,
  StepCardContent as CardContent,
  StepCardDescription as CardDescription,
  StepCardHeader as CardHeader,
  StepCardTitle as CardTitle,
} from "../portal-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ApplicationFormData } from "@/lib/schema/submission-schema";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";

interface ApplicationStepProps {
  opportunityData: Workspace;
  companyData: any;
  existingApplicationData?: any;
  onBack?: () => void;
  onComplete: () => void;
}

interface OpportunityQuestion {
  key: string;
  title: string;
  helper_text?: string;
  required?: boolean;
  display?: boolean;
}

// Fields collected as multi-select arrays in the Company Info step. They are
// already persisted on the Company document, and the OpportunityApplication
// schema types them as plain strings — so re-asking them here would both
// duplicate the question and break submit when more than one value is
// selected (mongoose CastError on multi-item arrays).
export const COMPANY_MULTI_SELECT_KEYS = ["revenue_model", "business_model"] as const;

export default function ApplicationStep({
  opportunityData,
  companyData,
  existingApplicationData,
  onBack,
}: ApplicationStepProps) {
  const { user } = useUser();
  const [formData, setFormData] = useState<{
    applicationData: ApplicationFormData;
  }>({ applicationData: {} });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [questions, setQuestions] = useState<OpportunityQuestion[]>([]);

  useEffect(() => {
    // Initialize form data with company info and existing application data
    if (companyData && companyData._id) {
      const initialData: ApplicationFormData = {};

      // First, populate with company data
      Object.keys(companyData).forEach((key) => {
        initialData[key] = companyData[key] || "";
      });

      // Then, override with existing application data if available
      if (existingApplicationData) {
        console.log(
          "🔄 Populating form with existing application data:",
          existingApplicationData
        );
        console.log(
          "🔑 Keys in existingApplicationData:",
          Object.keys(existingApplicationData)
        );

        Object.keys(existingApplicationData).forEach((key) => {
          // Skip internal fields like _id, created_on, etc.
          if (
            ![
              "_id",
              "created_on",
              "applicant_company",
              "pertains_to_opportunity",
              "changed_by",
              "opportunity_form_questions", // Skip the new backend field
            ].includes(key)
          ) {
            const value = existingApplicationData[key] || "";
            initialData[key] = value;
          } else {
            console.log("🔄 Skipping internal field:", key);
          }
        });
      }

      setFormData((prevData) => ({
        ...prevData,
        applicationData: { ...prevData.applicationData, ...initialData },
      }));
    }

    // Parse opportunity form questions
    if (
      opportunityData?.opportunity_form &&
      Object.keys(opportunityData.opportunity_form).length > 0
    ) {
      const questionsArray = Object.keys(opportunityData.opportunity_form).map(
        (key) => ({
          key,
          ...opportunityData.opportunity_form[key],
        })
      );
      console.log("❓ Parsed questions from opportunity form:", questionsArray);
      setQuestions(questionsArray);
    } else {
      // No default questions if none are configured
      setQuestions([]);
    }

    // Log opportunity form questions from backend if available
    if (existingApplicationData?.opportunity_form_questions) {
      console.log(
        "🔧 Backend opportunity form questions:",
        existingApplicationData.opportunity_form_questions
      );
    }
  }, [companyData, opportunityData, existingApplicationData]);

  const handleInputChange = (questionKey: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      applicationData: { ...prevData.applicationData, [questionKey]: value },
    }));

    // Clear error when user starts typing
    if (errors[questionKey]) {
      setErrors((prev) => ({ ...prev, [questionKey]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    questions.forEach((question) => {
      if (
        question.display &&
        question.required &&
        !COMPANY_MULTI_SELECT_KEYS.includes(
          question.key as (typeof COMPANY_MULTI_SELECT_KEYS)[number]
        )
      ) {
        if (
          !formData.applicationData[question.key] ||
          formData.applicationData[question.key].trim() === ""
        ) {
          newErrors[question.key] = "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Validate user is logged in
    if (!user || !user.id) {
      setErrors({
        submit:
          "You must be logged in to submit an application. Please go back and login.",
      });
      return;
    }

    // Validate company data before submitting
    if (!companyData || !companyData._id) {
      console.error("Company data is missing or invalid:", companyData);
      setErrors({
        submit:
          "Company information is missing. Please go back and complete the previous steps.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Submitting with company ID:", companyData._id);
      console.log("Submitting with user ID:", user.id);

      // Merge prefilled data from companyInfo with formData.applicationData
      const completeApplicationData = {
        ...companyData,
        ...formData.applicationData,
      };

      // Filter out any undefined, null, or empty fields AND immutable fields.
      // Also drop the multi-select array fields collected in the Company Info
      // step — they live on the Company document and would CastError when
      // mongoose tries to coerce a multi-item array onto the
      // OpportunityApplication's String-typed columns.
      const immutableFields = [
        "_id",
        "created_on",
        "applicant_company",
        "pertains_to_opportunity",
        "changed_by",
        ...COMPANY_MULTI_SELECT_KEYS,
      ];
      const filteredApplicationData = Object.fromEntries(
        Object.entries(completeApplicationData).filter(
          ([key, value]) =>
            value !== undefined &&
            value !== null &&
            value !== "" &&
            !immutableFields.includes(key)
        )
      );

      console.log(
        "🔧 Frontend filtered application data keys:",
        Object.keys(filteredApplicationData)
      );
      console.log("Submitting application data:", filteredApplicationData);

      // If we have existing application data, update it; otherwise create a new one
      if (existingApplicationData && existingApplicationData._id) {
        // Update existing application
        console.log(
          "🔄 Updating existing application:",
          existingApplicationData._id
        );
        await submissionApiClient.application.updateApplicationData(
          existingApplicationData._id,
          filteredApplicationData
        );
      } else {
        // Submit new opportunity application
        // Pass the Clerk user ID to ensure it's used for submission
        const userId = user?.id || null;
        console.log("🚀 Submitting application with user ID:", userId);
        try {
          const submitResponse =
            await submissionApiClient.application.submitApplication(
              companyData._id,
              opportunityData._id,
              filteredApplicationData, // Pass the application data
              userId as string // Pass the Clerk user ID
            );

          // If application already exists, fetch it and update it instead
          if (
            submitResponse.status === "exists" &&
            submitResponse.result?._id
          ) {
            console.log("ℹ️ Application exists, updating instead of creating");
            await submissionApiClient.application.updateApplicationData(
              submitResponse.result._id,
              filteredApplicationData
            );
          }
        } catch (submitError: any) {
          // If the error is about application existing, try to fetch and update it
          if (submitError.message?.includes("already exists")) {
            console.log("ℹ️ Application exists, fetching and updating...");
            try {
              const existingApp =
                await submissionApiClient.application.getApplicationData(
                  companyData._id,
                  opportunityData._id
                );
              if (existingApp.result?._id) {
                await submissionApiClient.application.updateApplicationData(
                  existingApp.result._id,
                  filteredApplicationData
                );
              } else {
                throw submitError; // Re-throw if we can't get the application
              }
            } catch (fetchError) {
              console.error("Error fetching existing application:", fetchError);
              throw submitError; // Re-throw original error
            }
          } else {
            throw submitError; // Re-throw if it's a different error
          }
        }
      }

      // Save LinkedIn URL as deal info (sequence generation will handle LinkedIn scraping)
      if (companyData._id && opportunityData._id) {
        const linkedInUrl =
          companyData.linkedIn ||
          formData.applicationData.linkedIn ||
          filteredApplicationData.linkedIn;

        if (linkedInUrl && linkedInUrl.trim() !== "") {
          console.log("🔗 Saving LinkedIn URL as deal info", {
            url: linkedInUrl,
            companyId: companyData._id,
            opportunityId: opportunityData._id,
          });

          // Save LinkedIn URL as deal info (non-blocking)
          // Sequence generation will automatically trigger LinkedIn scraping
          editDealInfo
            .editDealInfo({
              workspaceId: opportunityData._id,
              companyId: companyData._id,
              fieldName: "linkedIn",
              fieldValue: linkedInUrl.trim(),
            })
            .catch((error) => {
              console.error(
                "Error saving LinkedIn URL as deal info (non-blocking):",
                error
              );
            });
        }
      }

      setShowSuccessModal(true);
    } catch {
      setErrors({
        submit: "Failed to submit application. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    const baseUrl =
      import.meta.env.VITE_STARTUPFUEL_APP_URL ||
      "https://app.startupfuel.com";
    window.location.href = `${baseUrl}/funding?redirect=/funding`;
  };

  const getQuestionLabel = (question: OpportunityQuestion) => {
    return (
      <span>
        {question.title}
        {question.required && <span className="text-destructive ml-1">*</span>}
      </span>
    );
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
      <Card>
        <CardHeader>
          <CardTitle>Application</CardTitle>
          <CardDescription>
            {existingApplicationData ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-raising" />
                <span>
                  You have a saved application. You can review and update your
                  responses below.
                </span>
              </div>
            ) : (
              "Complete the application questionnaire for this opportunity. Fields marked with * are required."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Application Questions */}
          <div className="space-y-4">
            {questions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>No application questions available for this opportunity.</p>
              </div>
            ) : (
              questions
                .filter(
                  (question) =>
                    question.display &&
                    !COMPANY_MULTI_SELECT_KEYS.includes(
                      question.key as (typeof COMPANY_MULTI_SELECT_KEYS)[number]
                    )
                )
                .map((question) => (
                  <div key={question.key} className="space-y-2">
                    <Label htmlFor={question.key}>
                      {getQuestionLabel(question)}
                    </Label>
                    <Textarea
                      id={question.key}
                      value={formData.applicationData[question.key] || ""}
                      onChange={(e) =>
                        handleInputChange(question.key, e.target.value)
                      }
                      placeholder={
                        question.helper_text || "Enter your response..."
                      }
                      className="min-h-[100px]"
                    />
                    {errors[question.key] && (
                      <p className="text-sm text-destructive">
                        {errors[question.key]}
                      </p>
                    )}
                  </div>
                ))
            )}
          </div>

          {/* Submission Error */}
          {errors.submit && (
            <div className="p-3 bg-destructive/5 border border-destructive/30 rounded-md flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 pb-2 mt-4 border-t border-border/70">
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-card"></div>
                  {existingApplicationData
                    ? "Updating Application..."
                    : "Submitting Application..."}
                </div>
              ) : existingApplicationData ? (
                "Update Application"
              ) : (
                "Submit Application"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8 text-raising" />
              <DialogTitle>Application Submitted!</DialogTitle>
            </div>
            <DialogDescription className="text-left">
              Your application has been successfully{" "}
              {existingApplicationData ? "updated" : "submitted"} for{" "}
              <strong>{opportunityData.opportunity_name}</strong>.
              <br />
              <br />
              You will receive updates on your application status via email.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={handleSuccessClose} className="w-full">
              My Applications
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
